import { Fragment } from 'react';
import {
  makeStyles,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
  Overflow,
  OverflowItem,
  OverflowDivider,
  useOverflowMenu,
  useIsOverflowItemVisible,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  Tooltip,
} from '@fluentui/react-components';
import { MoreHorizontalRegular } from '@fluentui/react-icons';

export interface Crumb {
  /** Stable id used both as the React key and the Overflow item id. */
  id: string;
  name: string;
  current: boolean;
  onClick: () => void;
}

const useStyles = makeStyles({
  // Fill the (flex:1) parent and allow shrinking below content width, so the
  // ResizeObserver-driven Overflow can actually detect a too-narrow pane and
  // collapse the middle crumbs.
  bc: { width: '100%', minWidth: 0 },
  // Cap each segment's width so a single long name (e.g. a verbose project
  // title, which is pinned and never collapses into the "…" menu) can't push
  // the breadcrumb past the pane and clip the current-folder crumb. The full
  // name stays available via the `title` tooltip.
  crumbLabel: {
    display: 'block',
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

// One entry inside the "…" menu — rendered only while its breadcrumb crumb is
// hidden by the Overflow. The first and last crumbs are high-priority so they
// stay visible and therefore never appear here.
function OverflowCrumbMenuItem({ crumb }: { crumb: Crumb }) {
  const visible = useIsOverflowItemVisible(crumb.id);
  if (visible) return null;
  return <MenuItem onClick={crumb.onClick}>{crumb.name}</MenuItem>;
}

// The "…" control. Shown only when the Overflow is hiding at least one crumb;
// clicking it lists the hidden (intermediate) folders so any can be reached.
function OverflowCrumbMenu({ crumbs }: { crumbs: Crumb[] }) {
  const { ref, isOverflowing, overflowCount } = useOverflowMenu<HTMLButtonElement>();
  if (!isOverflowing) return null;
  // The divider is a SIBLING of the item, never a child — a BreadcrumbDivider
  // renders an <li>, and nesting it inside the item's <li> is invalid markup.
  return (
    <>
      <BreadcrumbItem>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Tooltip content="Dossiers intermédiaires" relationship="label">
              <Button
                ref={ref}
                appearance="subtle"
                size="small"
                icon={<MoreHorizontalRegular />}
                aria-label={`Afficher ${overflowCount} dossier(s) intermédiaire(s)`}
              />
            </Tooltip>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {crumbs.map((c) => (
                <OverflowCrumbMenuItem key={c.id} crumb={c} />
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>
      </BreadcrumbItem>
      <BreadcrumbDivider />
    </>
  );
}

/**
 * Breadcrumb that collapses its middle segments into a "…" menu when the pane
 * is too narrow to show them all. The first (project) and last (current folder)
 * crumbs are pinned; everything between them overflows into the menu, nearest
 * the start first — which is exactly where the "…" sits (right after the first
 * crumb). At least the first + last stay visible (`minimumVisible`).
 */
export function FolderBreadcrumb({ crumbs }: { crumbs: Crumb[] }) {
  const styles = useStyles();
  const lastIndex = crumbs.length - 1;
  return (
    <Overflow minimumVisible={2} overflowDirection="start">
      <Breadcrumb size="small" className={styles.bc}>
        {crumbs.map((c, i) => {
          const isLast = i === lastIndex;
          return (
            <Fragment key={c.id}>
              {/* First + last stay pinned (priority 2). Each item and the
                  divider that follows it share a groupId so the divider hides
                  in lockstep with its crumb — no stray chevrons on collapse. */}
              <OverflowItem id={c.id} groupId={c.id} priority={i === 0 || isLast ? 2 : 1}>
                <BreadcrumbItem>
                  <BreadcrumbButton current={c.current} onClick={c.onClick} title={c.name}>
                    <span className={styles.crumbLabel}>{c.name}</span>
                  </BreadcrumbButton>
                </BreadcrumbItem>
              </OverflowItem>
              {!isLast && (
                <OverflowDivider groupId={c.id}>
                  <BreadcrumbDivider />
                </OverflowDivider>
              )}
              {/* The "…" menu that reveals the hidden middle sits right after
                  the first crumb (where the collapsed segments begin). */}
              {i === 0 && <OverflowCrumbMenu crumbs={crumbs} />}
            </Fragment>
          );
        })}
      </Breadcrumb>
    </Overflow>
  );
}
